import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadLessonCover, validateImageFile, deleteLessonCover, initializeStorageBuckets } from '../../../utils/supabase/storage';

interface UploadableImageInputProps {
  currentImageUrl?: string | null;
  onImageUpload: (url: string) => void;
  onImageRemove?: () => void;
  label?: string;
  deleteOnRemove?: boolean;
}

export function UploadableImageInput({
  currentImageUrl,
  onImageUpload,
  onImageRemove,
  label = 'Image',
  deleteOnRemove = true,
}: UploadableImageInputProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>(currentImageUrl || '');
  const [inputId] = useState(() => `image-input-${Math.random().toString(36).substr(2, 9)}`);

  // Initialize storage buckets on component mount
  useEffect(() => {
    initializeStorageBuckets().catch(err => {
      console.error('Failed to initialize storage buckets:', err);
    });
  }, []);

  useEffect(() => {
    setImagePreview(currentImageUrl || '');
  }, [currentImageUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateImageFile(file);
    if (validationError) {
      toast.error(validationError);
      e.target.value = ''; // Reset input
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Supabase
    setIsUploading(true);
    try {
      const url = await uploadLessonCover(file);
      onImageUpload(url);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);

      // Provide more specific error messages
      let errorMsg = 'Failed to upload image. Please try again.';
      if (error?.message?.includes('not authenticated')) {
        errorMsg = 'Please sign in to upload images';
      } else if (error?.message?.includes('bucket')) {
        errorMsg = 'Storage bucket not available. Please contact support.';
      } else if (error?.message?.includes('permission')) {
        errorMsg = 'You do not have permission to upload files.';
      }

      toast.error(errorMsg);
      setImagePreview(currentImageUrl || '');
      e.target.value = ''; // Reset input
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (deleteOnRemove && currentImageUrl) {
      try {
        await deleteLessonCover(currentImageUrl);
      } catch (error) {
        console.error('Error deleting image:', error);
        // Continue with removal even if deletion fails
      }
    }
    setImagePreview('');
    if (onImageRemove) {
      onImageRemove();
    }
    // Reset file input
    const fileInput = document.getElementById(inputId) as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-2">
      <Label>{label} (optional)</Label>

      {!imagePreview ? (
        <label
          htmlFor={inputId}
          className="block border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
        >
          <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PNG, JPG, WEBP up to 5MB
          </p>
        </label>
      ) : (
        <div className="relative rounded-xl overflow-hidden border-2 border-border">
          <img
            src={imagePreview}
            alt="Preview"
            className="w-full h-40 object-cover"
          />
          <Button
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemoveImage}
          >
            <X className="w-4 h-4 mr-1" />
            Remove
          </Button>
        </div>
      )}

      <input
        type="file"
        accept="image/png, image/jpeg, image/jpg, image/webp"
        className="hidden"
        id={inputId}
        onChange={handleFileSelect}
        disabled={isUploading}
      />

      {isUploading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Uploading image...
        </div>
      )}
    </div>
  );
}
